import React, { useState, FC, useMemo } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { Pagination } from '@im/ui-lib/Pagination';
import CustomTable from 'src/components/TableComponent/TableComponent';
import {
  sageFilterQueryGenerator,
  SageTransactionsRequest,
  useGetViwSagetransctionlist,
} from './hooks/apiHooks';
import { ColumnFilter, ColumnSort } from 'src/components/TableComponent/types';

type PaginationState = {
  page: number;
  pageSize: number;
};

type SageTableState = {
  searchFilters?: ColumnFilter;
  sorting?: ColumnSort;
};

const INITIAL_PAGINATION: PaginationState = { page: 1, pageSize: 10 };

const ViewSageTransactions: FC = () => {
  const [pagination, setPagination] = useState(INITIAL_PAGINATION);
  const [tableState, setTableState] = useState<SageTableState>({});

  const searchFilters = tableState.searchFilters;
  const sorting = tableState.sorting;

  const setSearchFilters = (filters?: ColumnFilter) => {
    setTableState((previousState) => ({
      ...previousState,
      searchFilters: filters,
    }));
  };

  const setSorting = (sort?: ColumnSort) => {
    setTableState((previousState) => ({ ...previousState, sorting: sort }));
  };

  const defaultActions = [
    { label: 'Exact', value: 'exact' },
    { label: 'Starts with', value: 'startsWith' },
    { label: 'Contains', value: 'contains' },
    { label: 'Exclude', value: 'exclude' },
  ];
  const columnsConfig = [
    {
      field: 'reqId' as const,
      headerName: 'S.No.',
      sortable: true,
      width: 110,
    },
    {
      field: 'user_id' as const,
      filters: { actions: defaultActions },
      headerName: 'User ID',
      sortable: true,
      width: 180,
    },
    {
      field: 'session_id' as const,
      filters: { actions: defaultActions },
      headerName: 'Session ID',
      sortable: true,
      width: 220,
    },
    {
      field: 'invocation_id' as const,
      filters: { actions: defaultActions },
      headerName: 'Invocation ID',
      sortable: true,
      width: 220,
    },
    {
      field: 'author' as const,
      filters: { actions: defaultActions },
      headerName: 'Author',
      sortable: true,
      width: 140,
    },
    {
      field: 'timestamp' as const,
      filters: { actions: defaultActions },
      headerName: 'Timestamp',
      sortable: true,
      width: 200,
    },
    {
      field: 'content_text' as const,
      filters: { actions: defaultActions },
      headerName: 'Content',
      sortable: true,
      width: 300,
    },
    {
      field: 'country_code' as const,
      filters: { actions: defaultActions },
      headerName: 'Country Code',
      sortable: true,
      width: 150,
    },
  ];
  const sageQuery: SageTransactionsRequest = {
    page: pagination.page,
    page_size: pagination.pageSize,
    ...(sorting && {
      sortColumn: Object.keys(sorting)[0],
      sortDirection: sorting[Object.keys(sorting)[0]],
    }),
    ...sageFilterQueryGenerator(searchFilters),
  };

  const sageTransactions = useGetViwSagetransctionlist(sageQuery);

  const tableRows = useMemo(() => {
    if (!sageTransactions.data?.events) return [];

    return sageTransactions.data.events.map((item: any, idx: number) => ({
      author: item.author ?? '',
      content_text: item.content_text ?? '',
      country_code: item.country_code ?? '',
      invocation_id: item.invocation_id ?? '',
      reqId: idx + 1 + (sageTransactions.data.page - 1) * pagination.pageSize,
      session_id: item.session_id ?? '',
      timestamp: item.timestamp ?? '',
      user_id: item.user_id ?? '',
    }));
  }, [pagination.pageSize, sageTransactions.data]);

  const requestPagination = () => (
    <Pagination
      data-testid="sage-table-pagination"
      onPageChange={(page) => setPagination({ ...pagination, page })}
      onRowsPerPageChange={(pageSize) => setPagination({ page: 1, pageSize })}
      page={pagination.page}
      rowsPerPage={pagination.pageSize}
      totalCount={sageTransactions.data?.total_events ?? 0}
    />
  );
  return (
    <Box
      sx={{
        background: '#FFFFFF',
        border: '1px solid #CCCCCC',
        borderRadius: '16px',
        boxShadow: '0px 0px 9px 2px #00000021',
        padding: 2,
      }}
    >
      <Stack gap={2}>
        <CustomTable
          appliedFilters={searchFilters}
          columns={columnsConfig}
          data={tableRows}
          getRowId={(row) => row.reqId?.toString?.() ?? ''}
          isLoading={sageTransactions.isLoading}
          onFilterApply={(filter) => {
            const merged = { ...searchFilters, ...filter };
            const cleaned = Object.fromEntries(
              Object.entries(merged).filter(([_, v]) => v != null)
            );
            setSearchFilters(cleaned);
          }}
          onServerSortingChange={(sort) => setSorting(sort)}
          paginationComponent={requestPagination()}
          sx={{
            '& .MuiTableCell-head': {
              height: 50,
            },
          }}
        />
      </Stack>
    </Box>
  );
};
export default ViewSageTransactions;
